import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import HomeApi from '../../apiProvider/homeApi';
import { IMAGE_URL } from '../../network/apiClient';

const ProductsMain = () => {
    const navigate = useNavigate();
    const [groupedProducts, setGroupedProducts] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        window.scrollTo(0, 0);
        fetchProducts();
    }, []);

    const fetchProducts = async () => {
        try {
            setLoading(true);
            const response = await HomeApi.ProductList({ limit: 100 });
            if (response.status) {
                const products = response.response?.data || [];
                groupProducts(products);
            }
        } catch (error) {
            console.error("Error fetching products:", error);
        } finally {
            setLoading(false);
        }
    };

    const groupProducts = (products) => {
        const groups = products.reduce((acc, product) => {
            const categoryName = product.categoryIdDetails?.[0]?.name || "Other Products";
            if (!acc[categoryName]) {
                acc[categoryName] = {
                    name: categoryName,
                    description: product.categoryIdDetails?.[0]?.description || "",
                    products: []
                };
            }
            acc[categoryName].products.push(product);
            return acc;
        }, {});
        setGroupedProducts(groups);
    };

    const handleProductClick = (product) => {
        const categoryId = product.categoryIdDetails?.[0]?._id || "all";
        const subCategoryId = product.subCategoryDetails?.[0]?._id || "all";
        navigate(`/products/${categoryId}/${subCategoryId}`, {
            state: {
                productId: product._id,
                name: product.subCategoryDetails?.[0]?.name,
                description: product.subCategoryDetails?.[0]?.description,
                images: product.subCategoryDetails?.[0]?.images
            }
        });
    };

    return (
        <div style={{ paddingTop: '50px', backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
            <Helmet>
                <title>Products | Alpha Technical Rubber Products</title>
                <meta name="description" content="Explore our wide range of industrial rubber products, seals, and safety solutions." />
            </Helmet>

            {/* Hero Section */}
            <section className="text-white py-5 position-relative" style={{ background: 'linear-gradient(rgba(26, 34, 56, 0.9), rgba(26, 34, 56, 0.9)), url(/img/products-banner.png) center/cover', minHeight: '400px', display: 'flex', alignItems: 'center', marginTop: '30px' }}>
                <div className="container text-center">
                    <h1 className="fw-bold display-4 mb-3 text-white">Our Product Range</h1>
                    <p className="lead mb-0 text-white-100 mx-auto" style={{ maxWidth: '700px' }}>
                        High-performance sealing solutions, industrial rubber products, and safety systems designed for durability and precision.
                    </p>
                </div>
            </section>

            <div className="container py-5">
                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : Object.keys(groupedProducts).length > 0 ? (
                    Object.values(groupedProducts).map((category, catIdx) => (
                        <div key={catIdx} className="mb-5">
                            <div className="d-flex flex-column align-items-center mb-4 border-bottom pb-4 text-center">
                                <div className="bg-white p-3 rounded-circle shadow-sm mb-3 text-primary d-flex align-items-center justify-content-center" style={{ width: '60px', height: '60px' }}>
                                    <i className="bi bi-box-seam fs-3"></i>
                                </div>
                                <div>
                                    <h2 className="fw-bold text-primary mb-2 h3">{category.name}</h2>
                                    <p className="text-muted mb-0 small">{category.description}</p>
                                </div>
                            </div>

                            <div className="row justify-content-center g-4">
                                {category.products.map((prod) => (
                                    <div className="col-md-6 col-lg-3" key={prod._id}>
                                        <div
                                            onClick={() => handleProductClick(prod)}
                                            className="text-decoration-none h-100"
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <div className="card h-100 shadow-sm hover-card transition-all" style={{ borderRadius: '12px', overflow: 'hidden', border: '1px solid #dee2e6' }}>
                                                <div className="card-img-top bg-white p-3 d-flex align-items-center justify-content-center border-bottom" style={{ height: '180px' }}>
                                                    {prod.productImage?.[0] ? (
                                                        <img
                                                            src={`${IMAGE_URL}/${prod.productImage[0].docPath}/${prod.productImage[0].docName}`}
                                                            alt={prod.productName}
                                                            className="img-fluid"
                                                            style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain', transition: 'transform 0.3s ease' }}
                                                        />
                                                    ) : (
                                                        <i className="bi bi-box fs-1 text-muted"></i>
                                                    )}
                                                </div>
                                                <div className="card-body bg-white p-3 text-center">
                                                    <h5 className="fw-bold text-dark mb-3">{prod.productName}</h5>
                                                    <span className="btn btn-sm btn-industrial-primary product-btn">View Details</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="text-center py-5">
                        <h3 className="text-muted">No products found.</h3>
                    </div>
                )}
            </div>

            <style>{`
                .hover-card {
                    transition: transform 0.3s ease, box-shadow 0.3s ease;
                }
                .hover-card:hover {
                    transform: translateY(-8px);
                    box-shadow: 0 15px 30px rgba(0,0,0,0.1) !important;
                }
                .hover-card:hover img {
                    transform: scale(1.08);
                }
                .product-btn {
                    padding: 8px 16px !important;
                    font-size: 0.8rem !important;
                }
            `}</style>
        </div>
    );
};

export default ProductsMain;
